import classNames from 'classnames';
import { uniqueId } from 'lodash';
import { PropTypes as MobxPropTypes, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { FormGroup, Input, Label, UncontrolledTooltip } from 'reactstrap';

import CustomFormField from '../../../models/CustomFormField';
import FormField from '../../../models/FormField';
import { modelOf } from '../../../prop-types';
import BooleanFieldItem from '../../../types/BooleanFieldItem';
import Icon from '../../common/Icon';
import Field from '../Field';
import FieldError from '../FieldError';

// In the normal use cases, the form and field names don't change, and
// this only really affects the input/label binding, so not updating the id
// afterwards if props change should still be fine.
const createUniqueKey = (option, index) => {
  return uniqueId(`${option}--${index}`);
};

const RadioField = ({
  label,
  fieldName,
  field,
  options,
  tooltip,
  className,
  inline,
  validate,
  content,
  disabled,
  // unused, but no reason to pass to Field with rest
  formName,
  ...rest
}) => {
  const id = uniqueId(`${formName}__${fieldName}--`);

  const getTooltip = (text) => {
    return (
      <>
        <Icon name="info" className={`tooltip-${id}`} />
        <UncontrolledTooltip target={`.tooltip-${id}`}>
          {text}
        </UncontrolledTooltip>
      </>
    );
  };

  return (
    <FormGroup
      className={classNames('RadioField', className)}
      tag="fieldset"
      id={id}
    >
      {label && (
        <div className="RadioField__label">
          {label} {tooltip && getTooltip(tooltip)}
        </div>
      )}
      {options.map((option, index) => {
        const optionId = `${id}-${index}`;
        const isSelected = field.value === option.value;
        return (
          <FormGroup
            className={classNames('RadioFieldSet__radio-field', {
              'RadioFieldSet__radio-field-checked': isSelected,
            })}
            check={true}
            inline={inline}
            key={createUniqueKey(option.value, index)}
          >
            <div className="RadioFieldSet__inner-field">
              <Field
                aria-invalid={isSelected}
                component={Input}
                field={field}
                type="radio"
                name={fieldName}
                checked={isSelected}
                value={option.value}
                id={optionId}
                validate={validate}
                disabled={disabled || option.disabled}
                {...rest}
              />
              <Label check={true} for={optionId}>
                {option.label}
              </Label>
            </div>
            {content && content(option)}
            {option.content && option.content}
          </FormGroup>
        );
      })}
      <FieldError field={field} />
    </FormGroup>
  );
};

RadioField.propTypes = {
  field: PropTypes.oneOfType([modelOf(FormField), modelOf(CustomFormField)])
    .isRequired,
  formName: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  options: PropTypes.oneOfType([
    BooleanFieldItem,
    PropTypes.arrayOf(BooleanFieldItem),
    MobxPropTypes.observableArrayOf(BooleanFieldItem),
  ]).isRequired,
  className: PropTypes.string,
  tooltip: PropTypes.string,
  disabled: PropTypes.bool,
  inline: PropTypes.bool,
  validate: PropTypes.bool,
  content: PropTypes.func,
  label: PropTypes.node,
};

RadioField.defaultProps = {
  inline: true,
};

export default observer(RadioField);
